/**
 * RecoverPage — recuperarea contului cu fraza de recuperare.
 *
 * Pas 1: email + frază → derivă recovery_key, cere blob-ul de la server și
 *        decriptează cheia privată (dovada că fraza e corectă).
 * Pas 2: parolă master nouă, obligatorie (+ cod 2FA dacă e activ) → cheia
 *        privată e re-criptată cu noua master_key și trimisă la
 *        /auth/recovery/complete, împreună cu o frază de recuperare NOUĂ.
 * Pas 3: fraza nouă e afișată; cea folosită nu mai funcționează.
 *
 * Cheia privată trăiește doar în memoria acestei pagini, între cei doi pași.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { ApiError } from '@/api/client';
import RecoveryPhraseCard from '@/components/RecoveryPhraseCard';
import {
  buildRecoveryBundle,
  computeRecoveryAuthHash,
  deriveRecoveryKey,
  generateRecoveryPhrase,
  isValidRecoveryPhrase,
  openRecoveryBlob,
} from '@/crypto/recovery';
import {
  base64ToBytes,
  bytesToBase64,
  computeAuthHash,
  DEFAULT_KDF_PARAMS,
  deriveMasterKey,
  encryptSym,
  ensureSodium,
  generateSalt,
  kdfParamsFromServer,
  keyPairFromPrivateKey,
} from '@/crypto/sodium';
import { PasswordStrength } from '@/features/vault/PasswordTools';

const MIN_PASSWORD_LEN = 12;

interface Unlocked {
  email: string;
  privateKey: Uint8Array;
  recoveryAuthHash: string;
  requires2fa: boolean;
}

export default function RecoverPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [phrase, setPhrase] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [unlocked, setUnlocked] = useState<Unlocked | null>(null);
  const [issued, setIssued] = useState<{ phrase: string; email: string } | null>(null);

  // Zeroize la părăsirea paginii, indiferent cum.
  const unlockedRef = useRef<Unlocked | null>(null);
  unlockedRef.current = unlocked;
  useEffect(
    () => () => {
      unlockedRef.current?.privateKey.fill(0);
    },
    [],
  );

  const fail = (err: unknown) => {
    if (err instanceof ApiError) {
      setError(err.status === 401 ? t('recovery.invalid_phrase') : err.detail);
    } else {
      setError(t('recovery.invalid_phrase'));
    }
  };

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const normalizedEmail = email.trim().toLowerCase();
    if (!isValidRecoveryPhrase(phrase)) {
      setError(t('recovery.phrase_malformed'));
      return;
    }
    setBusy(true);
    try {
      await ensureSodium();
      const params = await authApi.recoveryParams(normalizedEmail);
      const recoveryKey = await deriveRecoveryKey(
        phrase,
        base64ToBytes(params.kdf_salt),
        kdfParamsFromServer(params.kdf_params),
      );
      try {
        const recoveryAuthHash = bytesToBase64(await computeRecoveryAuthHash(recoveryKey));
        const start = await authApi.recoveryStart(normalizedEmail, recoveryAuthHash);
        const privateKey = await openRecoveryBlob(
          recoveryKey,
          base64ToBytes(start.recovery_private_key_encrypted),
          base64ToBytes(start.recovery_private_key_nonce),
        );
        setUnlocked({
          email: normalizedEmail,
          privateKey,
          recoveryAuthHash,
          requires2fa: start.requires_2fa,
        });
        setPhrase('');
      } finally {
        recoveryKey.fill(0);
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const complete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!unlocked) return;
    setError(null);
    if (password.length < MIN_PASSWORD_LEN) {
      setError(t('recovery.password_too_short', { n: MIN_PASSWORD_LEN }));
      return;
    }
    if (password !== confirm) {
      setError(t('security_section.passwords_not_match'));
      return;
    }
    setBusy(true);
    // Fraza folosită se consumă — generăm una nouă, legată de aceeași cheie.
    const nextPhrase = generateRecoveryPhrase();
    try {
      const salt = await generateSalt();
      const masterKey = await deriveMasterKey(password, salt);
      try {
        const sealed = await encryptSym(unlocked.privateKey, masterKey);
        const trimmedCode = code.trim();
        const recovery = await buildRecoveryBundle(
          nextPhrase,
          await keyPairFromPrivateKey(unlocked.privateKey),
        );
        await authApi.recoveryComplete({
          recovery,
          email: unlocked.email,
          recovery_auth_hash: unlocked.recoveryAuthHash,
          auth_hash_new: bytesToBase64(await computeAuthHash(masterKey)),
          kdf_salt: bytesToBase64(salt),
          kdf_params: {
            algorithm: 'argon2id',
            mem_kib: DEFAULT_KDF_PARAMS.memlimit / 1024,
            iters: DEFAULT_KDF_PARAMS.opslimit,
            parallelism: 1,
            key_len: 32,
          },
          private_key_encrypted: bytesToBase64(sealed.ciphertext),
          private_key_nonce: bytesToBase64(sealed.nonce),
          ...(unlocked.requires2fa
            ? /^\d{6}$/.test(trimmedCode)
              ? { totp_code: trimmedCode }
              : { recovery_code: trimmedCode }
            : {}),
        });
      } finally {
        masterKey.fill(0);
      }
      unlocked.privateKey.fill(0);
      setPassword('');
      setConfirm('');
      // Înainte de login arătăm fraza nouă — cea veche nu mai funcționează.
      setIssued({ phrase: nextPhrase, email: unlocked.email });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError(t('recovery.generic_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-main-bg min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="display-title-lg">{t('app.name')}</h1>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            {t('recovery.page_tagline')}
          </p>
        </div>

        <div className="card">
          {issued ? (
            <div className="space-y-4" data-testid="recover-new-phrase">
              <div>
                <h2 className="display-title-sm mb-1">{t('recovery.recovered_title')}</h2>
                <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
                  {t('recovery.recovered_description')}
                </p>
              </div>
              <RecoveryPhraseCard phrase={issued.phrase} email={issued.email} />
              <button
                type="button"
                className="btn-primary w-full"
                data-testid="recover-continue"
                onClick={() =>
                  navigate(`/login?email=${encodeURIComponent(issued.email)}&recovered=1`, {
                    replace: true,
                  })
                }
              >
                {t('recovery.recovered_continue')}
              </button>
            </div>
          ) : !unlocked ? (
            <form onSubmit={unlock} className="space-y-4">
              <div>
                <h2 className="display-title-sm mb-1">{t('recovery.page_title')}</h2>
                <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
                  {t('recovery.page_description')}
                </p>
              </div>
              <label className="block">
                <span className="section-label no-dot mb-1.5">{t('auth.email')}</span>
                <input
                  type="email"
                  required
                  className="input"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
              </label>
              <label className="block">
                <span className="section-label no-dot mb-1.5">{t('recovery.phrase_label')}</span>
                <textarea
                  required
                  rows={3}
                  className="input font-mono text-sm"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  data-testid="recover-phrase"
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  disabled={busy}
                />
              </label>
              <FormError text={error} />
              <button type="submit" className="btn-primary w-full" disabled={busy}>
                {busy ? t('recovery.verifying') : t('recovery.verify_phrase')}
              </button>
            </form>
          ) : (
            <form onSubmit={complete} className="space-y-4">
              <div>
                <h2 className="display-title-sm mb-1">{t('recovery.new_password_title')}</h2>
                <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
                  {t('recovery.new_password_description')}
                </p>
              </div>
              <label className="block">
                <span className="section-label no-dot mb-1.5">
                  {t('recovery.new_password_label')}
                </span>
                <input
                  type="password"
                  required
                  className="input"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  autoFocus
                />
              </label>
              {password && <PasswordStrength password={password} />}
              <label className="block">
                <span className="section-label no-dot mb-1.5">
                  {t('security_section.confirm_password_placeholder')}
                </span>
                <input
                  type="password"
                  required
                  className="input"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={busy}
                />
              </label>
              {unlocked.requires2fa && (
                <label className="block">
                  <span className="section-label no-dot mb-1.5">
                    {t('recovery.two_factor_label')}
                  </span>
                  <input
                    type="text"
                    required
                    className="input font-mono"
                    autoComplete="one-time-code"
                    placeholder="000000 / XXXX-XXXX"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={busy}
                  />
                </label>
              )}
              <FormError text={error} />
              <button type="submit" className="btn-primary w-full" disabled={busy}>
                {busy ? t('recovery.saving_password') : t('recovery.set_password')}
              </button>
            </form>
          )}

          <div
            className="mt-6 pt-4 text-xs text-center"
            style={{ borderTop: '1px solid var(--border)', color: 'var(--text-dim)' }}
          >
            <Link to="/login" style={{ color: 'var(--accent)' }} className="hover:underline">
              {t('recovery.back_to_login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormError({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div
      className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2"
      role="alert"
    >
      {text}
    </div>
  );
}
